// Phase 6.3.1 — EmptyState primitive.
//
// The canonical "nothing here yet" surface. Replaces the ad-hoc,
// code-comment-voiced empty states the UX review flagged. Pure
// presentational — centered icon + title + optional description +
// optional single CTA. 6.3.2 migrates the real empty states onto it.

import { type ReactNode } from "react";

export type EmptyStateSize = "sm" | "md" | "lg";

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  size?: EmptyStateSize;
}

const SIZE: Record<
  EmptyStateSize,
  { pad: string; icon: string; gap: string }
> = {
  sm: { pad: "py-section px-loose", icon: "w-6 h-6", gap: "gap-snug" },
  md: { pad: "py-page px-section", icon: "w-8 h-8", gap: "gap-default" },
  lg: { pad: "py-16 px-section", icon: "w-10 h-10", gap: "gap-loose" },
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  size = "md",
}: EmptyStateProps) {
  const s = SIZE[size];
  return (
    <div
      data-testid="empty-state"
      data-size={size}
      className={`flex flex-col items-center text-center ${s.gap} ${s.pad}`}
    >
      {icon && (
        <div
          data-testid="empty-state-icon"
          className={`text-theme-text-secondary ${s.icon} flex items-center justify-center`}
        >
          {icon}
        </div>
      )}
      <h3 data-testid="empty-state-title" className="text-h3">
        {title}
      </h3>
      {description && (
        <p
          data-testid="empty-state-description"
          className="text-body-sm max-w-sm"
        >
          {description}
        </p>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          data-testid="empty-state-action"
          className="mt-snug inline-flex items-center gap-tight px-loose py-snug border border-theme-border-default text-caption text-theme-text-primary hover:border-theme-accent-bg hover:text-theme-accent-link transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
